import { useEffect, useMemo, useRef, useState } from "react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Calendar as CalendarIcon } from "../../icons/calendar";
import { Cash as CashIcon } from "../../icons/cash";
import { ChartBar as ChartBarIcon } from "../../icons/chart-bar";
import { ChartPie as ChartPieIcon } from "../../icons/chart-pie";
import { ChatAlt2 as ChatAlt2Icon } from "../../icons/chat-alt2";
import { ClipboardList as ClipboardListIcon } from "../../icons/clipboard-list";
import { CreditCard as CreditCardIcon } from "../../icons/credit-card";
import { Home as HomeIcon } from "../../icons/home";
import { LockClosed as LockClosedIcon } from "../../icons/lock-closed";
import { Mail as MailIcon } from "../../icons/mail";
import { MailOpen, MailOpen as MailOpenIcon } from "../../icons/mail-open";
import { Newspaper as NewspaperIcon } from "../../icons/newspaper";
import { OfficeBuilding as OfficeBuildingIcon } from "../../icons/office-building";
import { ReceiptTax as ReceiptTaxIcon } from "../../icons/receipt-tax";
import { Selector as SelectorIcon } from "../../icons/selector";
import { Share as ShareIcon } from "../../icons/share";
import { ShoppingBag as ShoppingBagIcon } from "../../icons/shopping-bag";
import { ShoppingCart as ShoppingCartIcon } from "../../icons/shopping-cart";
import { Truck as TruckIcon } from "../../icons/truck";
import { UserCircle as UserCircleIcon } from "../../icons/user-circle";
import { Users as UsersIcon } from "../../icons/users";
import { XCircle as XCircleIcon } from "../../icons/x-circle";
import { Logo } from "../logo";
import { Scrollbar } from "../scrollbar";
import { DashboardSidebarSection } from "./dashboard-sidebar-section";
import { OrganizationPopover } from "./organization-popover";
import { useAuth } from "../../hooks/use-auth";
import GroupsIcon from "@mui/icons-material/Groups";
import { Call } from "@mui/icons-material";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

const getSectionsAdministrador = (t) => [
  {
    title: t("General"),
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("Mi cuenta"),
        path: "/dashboard/mi-cuenta",
        icon: <UserCircleIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("CRM"),
    items: [
      {
        title: "Contactos",
        path: "/dashboard/contactos",
        icon: <UsersIcon fontSize="small" />,
      },
      {
        title: "Procesos de venta",
        path: "/dashboard/oportunidades",
        icon: <CashIcon fontSize="small" />,
      },
      {
        title: "Vendedores",
        path: "/dashboard/agentes",
        icon: <GroupsIcon fontSize="small" />,
      },
      {
        title: "Distribuidoras",
        path: "/dashboard/distribuidoras",
        icon: <EmojiTransportationIcon fontSize="small" />,
      },
      {
        title: "Vehículos",
        path: "/dashboard/vehiculos",
        icon: <TruckIcon fontSize="small" />,
      },
    ],
  },
  {
    title: "Rendimiento",
    items: [
      {
        title: "General",
        path: "/dashboard/reports/general",
        icon: <ChartBarIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("Apps"),
    items: [
      // {
      //   title: t('Actividades'),
      //   path: '/dashboard/actividades',
      //   icon: <ClipboardListIcon fontSize="small" />
      // },
      // {
      //   title: t('Mail'),
      //   path: '/dashboard/mail',
      //   icon: <MailOpen fontSize="small" />
      // },
      {
        title: "Plantillas de correo",
        path: "/dashboard/mailTemplate",
        icon: <MailIcon fontSize="small" />,
      },
      {
        title: t("Chat Interno"),
        path: "/dashboard/chat",
        icon: <ChatAlt2Icon fontSize="small" />,
      },
      {
        title: t("Calendar"),
        path: "/dashboard/calendar",
        icon: <CalendarIcon fontSize="small" />,
      },
      {
        title: "Buzón de sugerencias",
        path: "/dashboard/buzon",
        icon: <MailIcon fontSize="small" />,
      },
    ],
  },
];

const getSectionsDistribuidora = (t) => [
  {
    title: t("General"),
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("Mi cuenta"),
        path: "/dashboard/mi-cuenta",
        icon: <UserCircleIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("CRM"),
    items: [
      {
        title: "Contactos",
        path: "/dashboard/contactos",
        icon: <UsersIcon fontSize="small" />,
      },
      {
        title: "Procesos de venta",
        path: "/dashboard/oportunidades",
        icon: <CashIcon fontSize="small" />,
      },
      {
        title: "Agentes",
        path: "/dashboard/agentes",
        icon: <GroupsIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("Apps"),
    items: [
      // {
      //   title: t('Actividades'),
      //   path: '/dashboard/actividades',
      //   icon: <ClipboardListIcon fontSize="small" />
      // },
      // {
      //   title: t('Mail'),
      //   path: '/dashboard/mail',
      //   icon: <MailOpen fontSize="small" />
      // },
      {
        title: "Campañas de marketing",
        path: "/dashboard/campaigns",
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: "Plantillas de correo",
        path: "/dashboard/mailTemplate",
        icon: <MailIcon fontSize="small" />,
      },
      {
        title: t("Chat Interno"),
        path: "/dashboard/chat",
        icon: <ChatAlt2Icon fontSize="small" />,
      },
      {
        title: t("Calendar"),
        path: "/dashboard/calendar",
        icon: <CalendarIcon fontSize="small" />,
      },
      {
        title: "Buzón de sugerencias",
        path: "/dashboard/buzon",
        icon: <MailIcon fontSize="small" />,
      },
    ],
  },
];

const getSectionsAgente = (t) => [
  {
    title: t("General"),
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("Mi cuenta"),
        path: "/dashboard/mi-cuenta",
        icon: <UserCircleIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("CRM"),
    items: [
      {
        title: "Contactos",
        path: "/dashboard/contactos",
        icon: <UsersIcon fontSize="small" />,
      },
      {
        title: "Procesos de venta",
        path: "/dashboard/oportunidades",
        icon: <CashIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("Apps"),
    items: [
      // {
      //   title: t('Actividades'),
      //   path: '/dashboard/actividades',
      //   icon: <ClipboardListIcon fontSize="small" />
      // },
      // {
      //   title: t('Mail'),
      //   path: '/dashboard/mail',
      //   icon: <MailOpen fontSize="small" />
      // },
      {
        title: "Plantillas de correo",
        path: "/dashboard/mailTemplate",
        icon: <MailIcon fontSize="small" />,
      },
      {
        title: t("Chat Interno"),
        path: "/dashboard/chat",
        icon: <ChatAlt2Icon fontSize="small" />,
      },
      {
        title: t("Calendar"),
        path: "/dashboard/calendar",
        icon: <CalendarIcon fontSize="small" />,
      },
      {
        title: "Buzón de sugerencias",
        path: "/dashboard/buzon",
        icon: <MailIcon fontSize="small" />,
      },
    ],
  },
];

const getSectionsCallCenter = (t) => [
  {
    title: t("General"),
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("Mi cuenta"),
        path: "/dashboard/mi-cuenta",
        icon: <UserCircleIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("CRM"),
    items: [
      {
        title: "Seguimiento de contactos",
        path: "/dashboard/contactos/especiales",
        icon: <Call fontSize="small" />,
      },
      {
        title: "Leads de campañas",
        path: "/dashboard/solicitudes-contacto",
        icon: <UsersIcon fontSize="small" />,
      },
      {
        title: "Buzón de sugerencias",
        path: "/dashboard/buzon",
        icon: <MailIcon fontSize="small" />,
      },
      {
        title: "Campañas de marketing",
        path: "/dashboard/campaigns",
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: "Vendedores",
        path: "/dashboard/agentes",
        icon: <GroupsIcon fontSize="small" />,
      },
    ],
  },
  {
    title: "Rendimiento",
    items: [
      {
        title: "General",
        path: "/dashboard/reports/general",
        icon: <ChartBarIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("Apps"),
    items: [
      {
        title: t("Chat Interno"),
        path: "/dashboard/chat",
        icon: <ChatAlt2Icon fontSize="small" />,
      },
    ],
  },
];

const getSectionsFinance = (t) => [
  {
    title: t("General"),
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("Mi cuenta"),
        path: "/dashboard/mi-cuenta",
        icon: <UserCircleIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("CRM"),
    items: [
      {
        title: "Contactos con descuentos",
        path: "/dashboard/contactos/descuentos",
        icon: <UsersIcon fontSize="small" />,
      },
    ],
  },
  {
    title: t("Apps"),
    items: [
      {
        title: "Campañas de marketing",
        path: "/dashboard/campaigns",
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: t("Chat Interno"),
        path: "/dashboard/chat",
        icon: <ChatAlt2Icon fontSize="small" />,
      },
      {
        title: t("Calendar"),
        path: "/dashboard/calendar",
        icon: <CalendarIcon fontSize="small" />,
      },
    ],
  },
];

export const DashboardSidebar = (props) => {
  const { onClose, open } = props;
  const router = useRouter();
  const { user } = useAuth();
  const rolVisible =
    user?.role === "administrador"
      ? "Administrador"
      : user?.role === "distribuidora"
      ? "Distribuidora"
      : user?.role === "agente"
      ? "Agente"
      : user?.role === "callcenter"
      ? "Call Center"
      : user?.role === "callcenteradmin"
      ? "Call Center Admin"
      : user?.role === "finance"
      ? "Finanzas"
      : user?.role === "financeadmin"
      ? "Finanzas Admin"
      : "Usuario";

  const { t } = useTranslation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
    noSsr: true,
  });
  const sections = useMemo(() => {
    if (user?.role === "administrador") {
      return getSectionsAdministrador(t);
    }
    if (user?.role === "distribuidora") {
      return getSectionsDistribuidora(t);
    }
    if (user?.role === "agente") {
      return getSectionsAgente(t);
    }

    if (user?.role === "callcenter" || user?.role === "callcenteradmin") {
      return getSectionsCallCenter(t);
    }

    if (user?.role === "finance" || user?.role === "financeadmin") {
      return getSectionsFinance(t);
    }

    return [];
  }, [t, user]);

  const handlePathChange = () => {
    if (!router.isReady) {
      return;
    }

    if (open) {
      onClose?.();
    }
  };

  useEffect(
    handlePathChange,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady, router.asPath]
  );

  const content = (
    <>
      <Scrollbar
        sx={{
          height: "100%",
          "& .simplebar-content": {
            height: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div>
            <Box
              sx={{
                pt: 3,
                width: "60%",
                mx: "auto",
              }}
            >
              <NextLink href="/dashboard" passHref>
                <a>
                  <Logo variant="light" />
                </a>
              </NextLink>
            </Box>
          </div>
          <Divider
            sx={{
              borderColor: "#90C5D3",
              my: 1,
            }}
          />
          {user?.role !== "agente" && (
            <Box sx={{ p: 0.5 }}>
              <Typography
                color="neutral.100"
                variant="subtitle2"
                textAlign="center"
              >
                {rolVisible}
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            {sections.map((section) => (
              <DashboardSidebarSection
                key={section.title}
                path={router.asPath}
                sx={{
                  mt: 2,
                  "& + &": {
                    mt: 2,
                  },
                }}
                {...section}
              />
            ))}
          </Box>
          <Divider
            sx={{
              borderColor: "#90C5D3", // dark divider
            }}
          />
          <Box sx={{ p: 2 }}>
            <Typography color="neutral.100" variant="subtitle2">
              {t("Need Help?")}
            </Typography>
            <Typography color="neutral.500" variant="body2">
              {t("Contact support")}
            </Typography>
            <NextLink
              href="https://wa.me/message/BHLTE6XH2ULGL1"
              target="_blank"
              passHref
            >
              <Button
                color="secondary"
                component="a"
                fullWidth
                sx={{ mt: 2 }}
                variant="contained"
              >
                {t("Support")}
              </Button>
            </NextLink>
          </Box>
        </Box>
      </Scrollbar>
    </>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            backgroundColor: "neutral.900",
            borderRightColor: "divider",
            borderRightStyle: "solid",
            borderRightWidth: (theme) =>
              theme.palette.mode === "dark" ? 1 : 0,
            color: "#FFFFFF",
            width: 280,
            zIndex: (theme) => theme.zIndex.appBar + 100,
          },
        }}
        variant="temporary"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: "neutral.900",
          color: "#FFFFFF",
          width: 280,
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
